import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { API_V1, fetchApi, getImageUrl } from '../config/api';
import { useCart } from '../context/CartContext';
import { getExtraCartId } from '../utils/cartExtraId';
import { fallbackImageForExtra } from '../utils/extraImages';
import { refAmount } from '../utils/money';
import {
  WASABI_GINGER_OPTIONS,
  WASABI_GINGER_PIECES,
  emptyWasabiGingerQty,
  formatWasabiGingerSummary,
  isWasabiGingerExtra,
  wasabiGingerSelection,
  wasabiGingerTotal,
} from '../constants/wasabiGinger';
import './ProductDetail.css';
import rollDinamitaImage from '../assets/Tipos de Rolls/ROLL Camaron - Dinamita.png';

const ExtraDetail = () => {
  const { extraId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [extra, setExtra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [flavorId, setFlavorId] = useState('');
  const [pieceQty, setPieceQty] = useState(emptyWasabiGingerQty);

  useEffect(() => {
    if (!extraId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchApi(`${API_V1}/extras/${extraId}`)
      .then((res) => {
        if (!res.ok) throw new Error('Extra no encontrado');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const raw = data.data ?? data;
        const name = raw.title ?? raw.name ?? 'Extra';
        setExtra({
          id: raw.id ?? raw.extra_id,
          name,
          description: raw.description || '',
          price: refAmount(raw.price_eur),
          image:
            getImageUrl(raw.image_url ?? raw.image) ||
            fallbackImageForExtra(name) ||
            rollDinamitaImage,
          xetux_product_id: raw.xetux_product_id ?? null,
          xetux_item_id: raw.xetux_item_id ?? null,
          requiresFlavor: Boolean(raw.requires_flavor),
          flavors: Array.isArray(raw.flavors) ? raw.flavors : [],
          extras: Array.isArray(raw.extras) ? raw.extras : [],
        });
        setFlavorId('');
        setPieceQty(emptyWasabiGingerQty());
        setQuantity(1);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Error al cargar el extra');
          setExtra(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [extraId]);

  if (loading) {
    return (
      <section className="product-detail">
        <div className="container">
          <p className="combos-loading">Cargando extra…</p>
        </div>
      </section>
    );
  }

  if (error || !extra) {
    return (
      <section className="product-detail">
        <div className="container">
          <p className="combos-error">{error || 'Extra no encontrado.'}</p>
          <Link to="/categorias/extras">Volver a extras</Link>
        </div>
      </section>
    );
  }

  const selectedFlavor = extra.requiresFlavor
    ? extra.flavors.find((flavor) => String(flavor.product_id) === String(flavorId))
    : null;
  const choosesPieces = isWasabiGingerExtra(extra.name);
  const pieceTotal = wasabiGingerTotal(pieceQty);
  const piecesReady = !choosesPieces || pieceTotal === WASABI_GINGER_PIECES;
  const selection = choosesPieces ? wasabiGingerSelection(pieceQty) : [];
  const displayPrice = selectedFlavor
    ? Number(selectedFlavor.price).toFixed(2)
    : refAmount(extra.price);

  const updatePieceQty = (optionId, nextQty) => {
    const safe = Math.max(0, Math.floor(Number(nextQty) || 0));
    setPieceQty((prev) => {
      const others = WASABI_GINGER_OPTIONS.reduce((sum, option) => {
        if (option.id === optionId) return sum;
        return sum + (Number(prev[option.id]) || 0);
      }, 0);
      return {
        ...prev,
        [optionId]: Math.min(safe, Math.max(0, WASABI_GINGER_PIECES - others)),
      };
    });
  };

  const handleAddChildExtra = (child) => {
    const childName = child.title ?? child.name ?? '';
    const childId = child.id ?? child.extra_id;
    if (child.requires_flavor || isWasabiGingerExtra(childName)) {
      if (childId != null) navigate(`/extra/${childId}`);
      return;
    }
    addToCart({
      id: getExtraCartId('extra', extra.id, childId ?? '', childName),
      name: childName,
      price: refAmount(child.price_eur),
      quantity: 1,
      image: child.image_url
        ? getImageUrl(child.image_url)
        : fallbackImageForExtra(childName) || rollDinamitaImage,
      type: 'extra',
      extraId: childId,
      xetuxProductId: child.xetux_product_id ?? null,
      xetuxItemId: child.xetux_item_id ?? null,
    });
  };

  const handleAddToCart = () => {
    if (extra.requiresFlavor && !selectedFlavor) return;
    if (!piecesReady) return;
    const flavorName = selectedFlavor?.name ?? '';
    const mix = formatWasabiGingerSummary(selection);
    addToCart({
      id: getExtraCartId(
        'catalog',
        'extras',
        extra.id,
        selectedFlavor
          ? `${extra.name}-${selectedFlavor.product_id}`
          : choosesPieces
            ? `${extra.name}-${mix}`
            : extra.name
      ),
      name: selectedFlavor ? `${extra.name} — ${flavorName}` : extra.name,
      price: displayPrice,
      quantity,
      image: extra.image,
      type: 'extra',
      extraId: extra.id,
      xetuxProductId: selectedFlavor ? selectedFlavor.product_id : extra.xetux_product_id,
      xetuxItemId: selectedFlavor ? selectedFlavor.item_id : extra.xetux_item_id,
      accompaniments: choosesPieces ? selection : undefined,
    });
  };

  return (
    <section className="product-detail">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Inicio</Link>
          <span> / </span>
          <Link to="/categorias">CATEGORÍAS</Link>
          <span> / </span>
          <Link to="/categorias/extras">EXTRAS</Link>
          <span> / </span>
          <span>{extra.name}</span>
        </div>

        <div className="product-detail-content">
          <div className="product-detail-left">
            <div className="product-detail-image-container">
              <img
                src={extra.image}
                alt={extra.name}
                className="product-detail-image"
                onError={(e) => {
                  const fallback = fallbackImageForExtra(extra.name);
                  if (fallback && e.currentTarget.src !== fallback) {
                    e.currentTarget.src = fallback;
                    return;
                  }
                  e.currentTarget.src = rollDinamitaImage;
                }}
              />
            </div>
          </div>
          <div className="product-detail-right">
            <h1 className="product-detail-name">{extra.name}</h1>
            {extra.description ? (
              <p className="product-detail-description">{extra.description}</p>
            ) : null}
            {extra.requiresFlavor ? (
              <div className="flavor-field">
                <label htmlFor="extra-flavor">Sabor</label>
                <select
                  id="extra-flavor"
                  className="flavor-select"
                  value={flavorId}
                  onChange={(event) => setFlavorId(event.target.value)}
                >
                  <option value="">Elige un sabor</option>
                  {extra.flavors.map((flavor) => (
                    <option key={flavor.product_id} value={flavor.product_id}>
                      {flavor.name} — Ref {Number(flavor.price).toFixed(2)}
                    </option>
                  ))}
                </select>
                {extra.flavors.length === 0 ? (
                  <p className="flavor-empty">No hay sabores disponibles en este momento.</p>
                ) : null}
              </div>
            ) : null}
            {choosesPieces ? (
              <div className="combo-included-section">
                <p className="combo-included-hint">
                  Elige tu combinación: {WASABI_GINGER_PIECES} piezas entre wasabi y
                  jengibre. Puedes pedir uno de cada, dos wasabis o dos jengibres. El
                  precio sigue siendo el mismo.
                </p>
                <p className={`combo-sauce-sum ${piecesReady ? 'is-ok' : 'is-pending'}`}>
                  Seleccionadas: {pieceTotal} / {WASABI_GINGER_PIECES}
                </p>
                <div className="combo-sauce-list">
                  {WASABI_GINGER_OPTIONS.map((option) => {
                    const qty = Number(pieceQty[option.id]) || 0;
                    return (
                      <div key={option.id} className="combo-sauce-row">
                        <span className="combo-sauce-name">{option.name}</span>
                        <div className="combo-sauce-qty">
                          <button
                            type="button"
                            className="combo-sauce-qty-btn"
                            onClick={() => updatePieceQty(option.id, qty - 1)}
                            disabled={qty <= 0}
                            aria-label={`Menos ${option.name}`}
                          >
                            −
                          </button>
                          <span className="combo-sauce-qty-value">{qty}</span>
                          <button
                            type="button"
                            className="combo-sauce-qty-btn"
                            onClick={() => updatePieceQty(option.id, qty + 1)}
                            disabled={pieceTotal >= WASABI_GINGER_PIECES}
                            aria-label={`Más ${option.name}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <div className="product-price">
              <span className="price-label">Ref</span>
              <span className="price-value">{displayPrice || '—'}</span>
            </div>
            <div className="product-actions">
              <div className="quantity-selector">
                <button
                  type="button"
                  className="quantity-btn"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="quantity-value">{quantity}</span>
                <button
                  type="button"
                  className="quantity-btn"
                  onClick={() => setQuantity((q) => q + 1)}
                >
                  +
                </button>
              </div>
              <button
                type="button"
                className="add-to-cart-main-btn"
                onClick={handleAddToCart}
                disabled={(extra.requiresFlavor && !selectedFlavor) || !piecesReady}
              >
                Añadir al Carrito
              </button>
            </div>
            {Array.isArray(extra.extras) && extra.extras.length > 0 ? (
              <div className="extra-sauces">
                <h3 className="extra-sauces-title">Extras</h3>
                <div className="sauces-list">
                  {extra.extras.map((child) => {
                    const childName = child.title ?? child.name ?? 'Extra';
                    const childImage = child.image_url
                      ? getImageUrl(child.image_url)
                      : fallbackImageForExtra(childName);
                    return (
                      <div key={child.id ?? child.extra_id} className="sauce-item">
                        <div className="sauce-icon">
                          {childImage ? (
                            <img
                              src={childImage}
                              alt={childName}
                              className="sauce-image"
                              onError={(e) => {
                                const fallback = fallbackImageForExtra(childName);
                                if (fallback && e.currentTarget.src !== fallback) {
                                  e.currentTarget.src = fallback;
                                }
                              }}
                            />
                          ) : null}
                        </div>
                        <div className="sauce-info">
                          <span className="sauce-name">{childName}</span>
                          <span className="sauce-price">Ref {refAmount(child.price_eur)}</span>
                        </div>
                        <button
                          type="button"
                          className="sauce-add-btn"
                          onClick={() => handleAddChildExtra(child)}
                        >
                          {child.requires_flavor
                            ? 'Elegir sabor'
                            : isWasabiGingerExtra(childName)
                              ? 'Elegir combinación'
                              : 'Añadir al Carrito'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExtraDetail;
